/**
 * O PAINEL DAS REGRAS — T17.
 *
 * *"todas as regras do hqlab devem ser possiveis editar elas (como restrição de palavras ou
 * fluxos) num painel onde um perfil com autorização administrativa consiga mexer. Nada deve ta
 * chumbado no código. Tudo deve está num banco de dados e que possa ser editado pelo painel
 * administrativo"*
 *
 * ── O CAMINHO QUE UMA MUDANÇA FAZ ───────────────────────────────────────────────────────────
 *
 *   esta tela  →  confere no serviço  →  grava no banco  →  manda o serviço reler  →  vale
 *
 * Os quatro passos aparecem na tela, e o último é confirmado lendo de volta o que o SERVIÇO está
 * usando — não o que o banco guardou. São coisas diferentes, e a diferença é justamente o que
 * alguém descobriria tarde: gravar e achar que valeu.
 *
 * ── A CONFERÊNCIA É A DO SERVIÇO, E NÃO UMA CÓPIA DELA ──────────────────────────────────────
 *
 * O botão de conferir chama o backend. Reimplementar a validação aqui daria duas validações para a
 * mesma regra, que divergem no primeiro campo novo — e aí o painel aceita o que o serviço depois
 * recusa, e quem salvou acha que valeu.
 *
 * ── E O RESUMO DO QUE MUDA APARECE ANTES DE SALVAR ──────────────────────────────────────────
 *
 * Tirar as regras do código tirou junto o commit, a revisão e o deploy. A revisão não some: ela
 * vira a lista "o que vai mudar", em português, com o que AFROUXA a barreira em destaque. Apagar
 * 133 termos sem ler "133 termos A MENOS" exige ignorar a frase, e não só errar um clique.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Firebase, { auth } from "../../api/firebase.class";
import { souAdministrador } from "../../admin/o-acesso-ao-painel";
import {
  CATEGORIAS,
  COLECAO_DAS_REGRAS,
  DOCUMENTO_DAS_REGRAS,
  Categoria,
  FORMAS,
  Forma,
  NOME_DA_CATEGORIA,
  O_QUE_O_LIMIAR_FAZ,
  O_QUE_O_NUMERO_FAZ,
  Regras,
  SEVERIDADES,
  Severidade,
  TermoDeRegra,
  comOTermoNovo,
  comOTermoTrocado,
  filtrarTermos,
  houveMudanca,
  oQueMudou,
  paraOBanco,
  semOTermo,
  termoEmBranco,
} from "../../admin/as-regras-no-painel";
import "./regras.scss";

const SERVICO = process.env.REACT_APP_BACKEND_ROUTE;

type Passo = "" | "conferindo" | "gravando" | "recarregando" | "pronto" | "recusado" | "falhou";

export default function PainelDeRegras() {
  const [podeMexer, setPodeMexer] = useState<boolean | null>(null);
  const [original, setOriginal] = useState<Regras | null>(null);
  const [editando, setEditando] = useState<Regras | null>(null);
  const [deOnde, setDeOnde] = useState<string>("");
  const [busca, setBusca] = useState("");
  const [novo, setNovo] = useState<TermoDeRegra>(termoEmBranco());
  const [passo, setPasso] = useState<Passo>("");
  const [problemas, setProblemas] = useState<string[]>([]);
  const [recado, setRecado] = useState("");

  const carregar = useCallback(async () => {
    try {
      const r = await fetch(`${SERVICO}/regras`);
      if (!r.ok) throw new Error(`o serviço respondeu ${r.status}`);
      const corpo = await r.json();
      setOriginal(corpo.regras);
      setEditando(JSON.parse(JSON.stringify(corpo.regras)));
      setDeOnde(
        corpo.origem === "banco"
          ? `lidas do banco, versão ${corpo.versao}`
          : "ainda não há regras no banco — o serviço está usando a lista que veio com ele"
      );
    } catch (e) {
      setRecado(`Não consegui ler as regras do serviço: ${e instanceof Error ? e.message : String(e)}`);
    }
  }, []);

  useEffect(() => {
    let vivo = true;
    // As duas perguntas são independentes e vão em paralelo: quem é você, e o que está valendo.
    void souAdministrador().then((v) => vivo && setPodeMexer(v));
    void carregar();
    return () => {
      vivo = false;
    };
  }, [carregar]);

  const mudancas = useMemo(
    () => (original && editando ? oQueMudou(original, editando) : []),
    [original, editando]
  );
  const mudou = !!original && !!editando && houveMudanca(original, editando);
  const termosNaTela = useMemo(
    () => (editando ? filtrarTermos(editando.palavras.termos, busca) : []),
    [editando, busca]
  );

  /** Mexer num termo pede a posição REAL, e não a da lista filtrada. */
  const posicaoReal = (t: TermoDeRegra) => editando!.palavras.termos.indexOf(t);

  function trocarTermos(termos: TermoDeRegra[]) {
    setEditando({ ...editando!, palavras: { ...editando!.palavras, termos } });
  }

  async function salvar() {
    if (!editando) return;
    setProblemas([]);
    setRecado("");

    setPasso("conferindo");
    const conferencia = await fetch(`${SERVICO}/regras/conferir`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(editando),
    }).then((r) => r.json());
    if (!conferencia.aceito) {
      setProblemas(conferencia.problemas);
      setPasso("recusado");
      return;
    }

    setPasso("gravando");
    try {
      const quem = auth?.currentUser?.email || auth?.currentUser?.uid || "desconhecido";
      // A versão sobe a cada gravação: é o que permite dizer "o serviço está na 7 e o banco na 8"
      // quando alguém salvou e a mudança não apareceu.
      const paraGravar = { ...editando, versao: editando.versao + 1 };
      await Firebase.setDocument(COLECAO_DAS_REGRAS, DOCUMENTO_DAS_REGRAS, paraOBanco(paraGravar, quem));
    } catch (e) {
      setRecado(
        "O banco recusou a gravação. Se a mensagem fala em permissão, a sua conta não está na " +
          `lista de administradores. (${e instanceof Error ? e.message : String(e)})`
      );
      setPasso("falhou");
      return;
    }

    setPasso("recarregando");
    await fetch(`${SERVICO}/regras/recarregar`, { method: "POST" }).catch(() => undefined);
    // Lê de volta O QUE O SERVIÇO ESTÁ USANDO, e não o que o banco guardou: gravar e achar que
    // valeu é o erro que esta tela existe para não deixar acontecer.
    await carregar();
    setPasso("pronto");
  }

  if (podeMexer === false) {
    return (
      <main className="regras-painel">
        <h1>Regras do HQ-Lab</h1>
        <p className="regras-sem-acesso">
          Esta página é de quem tem autorização administrativa. A sua conta não está na lista.
        </p>
        <p>
          <Link to="/">Voltar ao início</Link>
        </p>
      </main>
    );
  }

  if (!editando || !original) {
    return (
      <main className="regras-painel">
        <h1>Regras do HQ-Lab</h1>
        <p>{recado || "Lendo as regras que estão valendo…"}</p>
      </main>
    );
  }

  return (
    <main className="regras-painel">
      <h1>Regras do HQ-Lab</h1>
      <p className="regras-de-onde">{deOnde}</p>
      <p className="regras-explicacao">
        O que está nesta página é o que a plataforma usa agora. Salvar grava no banco e manda o
        serviço reler — não precisa de publicação nem de reinício.
      </p>

      {/* ── AS PALAVRAS ──────────────────────────────────────────────────────────── */}
      <section>
        <h2>Palavras barradas</h2>
        <p className="regras-conta">
          {editando.palavras.termos.length} termos e {editando.palavras.frases.length} frases.
        </p>

        <label className="regras-busca">
          Procurar
          <input
            type="search"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="palavra, categoria ou contexto"
          />
        </label>

        <fieldset className="regras-novo">
          <legend>Acrescentar um termo</legend>
          <input
            aria-label="O termo"
            value={novo.termo}
            onChange={(e) => setNovo({ ...novo, termo: e.target.value })}
            placeholder="a palavra"
          />
          <select
            aria-label="Como ele casa"
            value={novo.forma}
            onChange={(e) => setNovo({ ...novo, forma: e.target.value as Forma })}
          >
            <option value="radical">começa com (pega plural e diminutivo)</option>
            <option value="exato">a palavra inteira</option>
          </select>
          <select
            aria-label="Categoria"
            value={novo.categoria ?? "sexual"}
            onChange={(e) => setNovo({ ...novo, categoria: e.target.value as Categoria })}
          >
            {CATEGORIAS.map((c) => (
              <option key={c} value={c}>
                {NOME_DA_CATEGORIA[c]}
              </option>
            ))}
          </select>
          <button
            type="button"
            disabled={!novo.termo.trim()}
            onClick={() => {
              trocarTermos(comOTermoNovo(editando.palavras.termos, { ...novo, termo: novo.termo.trim() }));
              setNovo(termoEmBranco());
            }}
          >
            Acrescentar
          </button>
        </fieldset>

        <ul className="regras-termos">
          {termosNaTela.slice(0, 200).map((t) => {
            const i = posicaoReal(t);
            return (
              <li key={`${t.termo}-${t.forma}-${i}`}>
                <input
                  aria-label={`Termo ${t.termo}`}
                  value={t.termo}
                  onChange={(e) => trocarTermos(comOTermoTrocado(editando.palavras.termos, i, { ...t, termo: e.target.value }))}
                />
                <select
                  aria-label={`Como "${t.termo}" casa`}
                  value={t.forma}
                  onChange={(e) =>
                    trocarTermos(comOTermoTrocado(editando.palavras.termos, i, { ...t, forma: e.target.value as Forma }))
                  }
                >
                  {FORMAS.map((f) => (
                    <option key={f} value={f}>
                      {f === "radical" ? "começa com" : "palavra inteira"}
                    </option>
                  ))}
                </select>
                <select
                  aria-label={`Categoria de "${t.termo}"`}
                  value={t.categoria ?? "sexual"}
                  onChange={(e) =>
                    trocarTermos(
                      comOTermoTrocado(editando.palavras.termos, i, { ...t, categoria: e.target.value as Categoria })
                    )
                  }
                >
                  {CATEGORIAS.map((c) => (
                    <option key={c} value={c}>
                      {NOME_DA_CATEGORIA[c]}
                    </option>
                  ))}
                </select>
                <select
                  aria-label={`Severidade de "${t.termo}"`}
                  value={t.severidade}
                  onChange={(e) =>
                    trocarTermos(
                      comOTermoTrocado(editando.palavras.termos, i, { ...t, severidade: e.target.value as Severidade })
                    )
                  }
                >
                  {SEVERIDADES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
                {t.contextoOk?.length ? (
                  <span className="regras-contexto" title="Palavras que tornam este termo legítimo na mesma frase">
                    passa com: {t.contextoOk.join(", ")}
                  </span>
                ) : null}
                <button type="button" className="regras-tirar" onClick={() => trocarTermos(semOTermo(editando.palavras.termos, i))}>
                  Tirar
                </button>
              </li>
            );
          })}
        </ul>
        {termosNaTela.length > 200 && (
          <p className="regras-conta">
            Mostrando 200 de {termosNaTela.length}. Use a busca para chegar no que você procura.
          </p>
        )}
      </section>

      {/* ── AS MENSAGENS ─────────────────────────────────────────────────────────── */}
      <section>
        <h2>O que a criança lê quando a barreira fecha</h2>
        {CATEGORIAS.map((c) => (
          <label key={c} className="regras-mensagem">
            {NOME_DA_CATEGORIA[c]}
            <textarea
              value={editando.palavras.mensagens[c]}
              rows={3}
              onChange={(e) =>
                setEditando({
                  ...editando,
                  palavras: { ...editando.palavras, mensagens: { ...editando.palavras.mensagens, [c]: e.target.value } },
                })
              }
            />
          </label>
        ))}
      </section>

      {/* ── OS NÚMEROS ───────────────────────────────────────────────────────────── */}
      <section>
        <h2>O pedido que vai ao desenhista</h2>
        {(Object.keys(editando.desenho) as (keyof Regras["desenho"])[]).map((k) => (
          <label key={k} className="regras-numero">
            <span className="regras-numero-nome">{k}</span>
            <input
              type="number"
              value={editando.desenho[k]}
              onChange={(e) => setEditando({ ...editando, desenho: { ...editando.desenho, [k]: Number(e.target.value) } })}
            />
            <span className="regras-numero-diz">{O_QUE_O_NUMERO_FAZ[k]}</span>
          </label>
        ))}
      </section>

      {/* ── T24 · OS ESTILOS DO DESENHO ─────────────────────────────────────────────
          «na interface vai aparecer só massinha, mas o que a gente envia é um prompt detalhado
          (...) no painel administrativo tem que ter o equivalente do que é cada estilo». O nome é
          o que a criança vê na lista; os três textos são o que vai ao desenhista. */}
      {editando.pedidoDeDesenho && (
        <section className="regras-estilos">
          <h2>Os estilos do desenho</h2>
          <p className="regras-conta">
            O nome é o que aparece na lista da janela de gerar. Os textos abaixo dele são o que vai, de fato, ao
            desenhista — em inglês, porque é a língua que ele lê.
          </p>
          {[
            {
              tag: "casa",
              rotulo: editando.pedidoDeDesenho.rotuloDaCasa ?? "",
              assuntoPersonagem: editando.pedidoDeDesenho.assuntoPersonagem,
              assuntoCenario: editando.pedidoDeDesenho.assuntoCenario,
              esteticaTronco: editando.pedidoDeDesenho.esteticaTronco,
            },
            ...Object.entries(editando.pedidoDeDesenho.estilos ?? {}).map(([tag, e]) => ({ tag, ...e })),
          ].map((est) => {
            const trocar = (campo: string, valor: string) => {
              const pd = editando.pedidoDeDesenho!;
              if (est.tag === "casa") {
                const chave = campo === "rotulo" ? "rotuloDaCasa" : campo;
                setEditando({ ...editando, pedidoDeDesenho: { ...pd, [chave]: valor } });
              } else {
                const estilos = pd.estilos ?? {};
                setEditando({
                  ...editando,
                  pedidoDeDesenho: { ...pd, estilos: { ...estilos, [est.tag]: { ...estilos[est.tag], [campo]: valor } } },
                });
              }
            };
            return (
              <fieldset key={est.tag} className="regras-estilo">
                <legend>{est.tag === "casa" ? "O estilo da casa (o padrão)" : est.rotulo || est.tag}</legend>
                <label className="regras-mensagem">
                  Nome na lista
                  <input id={`estilo-${est.tag}-rotulo`} value={est.rotulo} onChange={(e) => trocar("rotulo", e.target.value)} />
                </label>
                <label className="regras-mensagem">
                  Quando é personagem
                  <textarea id={`estilo-${est.tag}-personagem`} rows={2} value={est.assuntoPersonagem} onChange={(e) => trocar("assuntoPersonagem", e.target.value)} />
                </label>
                <label className="regras-mensagem">
                  Quando é cenário
                  <textarea id={`estilo-${est.tag}-cenario`} rows={2} value={est.assuntoCenario} onChange={(e) => trocar("assuntoCenario", e.target.value)} />
                </label>
                <label className="regras-mensagem">
                  A estética (traço, cor, textura)
                  <textarea id={`estilo-${est.tag}-estetica`} rows={3} value={est.esteticaTronco} onChange={(e) => trocar("esteticaTronco", e.target.value)} />
                </label>
              </fieldset>
            );
          })}
        </section>
      )}

      <section>
        <h2>A conferência do desenho pronto</h2>
        {(Object.keys(editando.barreiraDoDesenho) as (keyof Regras["barreiraDoDesenho"])[]).map((k) => (
          <label key={k} className="regras-numero">
            <span className="regras-numero-nome">{k}</span>
            <input
              type="number"
              step="0.01"
              min="0"
              max="1"
              value={editando.barreiraDoDesenho[k]}
              onChange={(e) =>
                setEditando({
                  ...editando,
                  barreiraDoDesenho: { ...editando.barreiraDoDesenho, [k]: Number(e.target.value) },
                })
              }
            />
            <span className="regras-numero-diz">{O_QUE_O_LIMIAR_FAZ[k]}</span>
          </label>
        ))}
      </section>

      {/* ── O QUE VAI MUDAR, E SÓ DEPOIS O BOTÃO ─────────────────────────────────── */}
      <section className="regras-fechar">
        <h2>O que vai mudar</h2>
        {!mudou && <p>Nada mudou ainda.</p>}
        {mudou && (
          <ul className="regras-mudancas">
            {mudancas.map((m, i) => (
              <li key={i} className={m.afrouxa ? "regras-afrouxa" : undefined}>
                {m.texto}
                {m.afrouxa && <span className="regras-aviso-afrouxa"> — isto deixa passar o que hoje é barrado</span>}
              </li>
            ))}
          </ul>
        )}

        {problemas.length > 0 && (
          <div className="regras-problemas">
            <p>O serviço recusou, e nada foi gravado:</p>
            <ul>
              {problemas.map((p, i) => (
                <li key={i}>{p}</li>
              ))}
            </ul>
          </div>
        )}
        {recado && <p className="regras-problemas">{recado}</p>}
        {passo === "pronto" && <p className="regras-pronto">Gravado, e o serviço já está usando. {deOnde}.</p>}

        <div className="regras-botoes">
          <button type="button" disabled={!mudou || passo === "conferindo" || passo === "gravando"} onClick={salvar}>
            {passo === "conferindo" && "Conferindo…"}
            {passo === "gravando" && "Gravando…"}
            {passo === "recarregando" && "Mandando o serviço reler…"}
            {(passo === "" || passo === "pronto" || passo === "recusado" || passo === "falhou") && "Salvar as regras"}
          </button>
          <button
            type="button"
            disabled={!mudou}
            onClick={() => {
              setEditando(JSON.parse(JSON.stringify(original)));
              setProblemas([]);
              setRecado("");
              setPasso("");
            }}
          >
            Desfazer tudo
          </button>
        </div>
      </section>
    </main>
  );
}
